import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../api";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/hub" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(username.trim(), password);
      navigate("/hub");
    } catch (e2) {
      // Differentiate the three failure modes so staff understand what to do.
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail;
      if (status === 401) {
        setErr("Wrong username or password. Try again — caps lock on?");
      } else if (status === 403) {
        setErr(formatApiError(detail) || "Account is disabled. Contact your admin.");
      } else if (!e2?.response) {
        // No response = network error / timeout / backend down
        setErr("Can't reach the server. Check your internet and try again in a few seconds.");
      } else if (status >= 500) {
        setErr("Server is busy right now. We retried once — please try again in a moment.");
      } else {
        setErr(formatApiError(detail) || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0e1a] text-slate-100" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] p-12 border-r hairline surface-1 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-600/90 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight">Mercy Management System</div>
              <div className="text-xs text-slate-400">Sign in</div>
            </div>
          </div>
        </div>
        <div className="relative z-10">
          <div className="text-5xl font-semibold leading-tight tracking-tight">
            Mercy Management<br/>
            <span className="text-blue-400">System</span>
          </div>
          <p className="mt-6 text-slate-400 max-w-md">
            Internal operations for Mercy Skills Consultancy. Candidates, leads, clients, attendance, and sales solutions in one workspace.
          </p>
          <div className="mt-10 text-xs text-slate-500 num">
            Trusted Foreign Worker Recruitment · Malaysia · Licence JTKSM 928
          </div>
        </div>
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(#3b82f6 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6 fade-in" data-testid="login-form">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
            <p className="text-sm text-slate-400 mt-1">Use your staff credentials to continue.</p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="username" className="text-slate-300">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                placeholder="e.g. jonathan"
                className="mt-1.5 bg-[#0f1626] border-slate-800 focus-visible:ring-blue-600"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-[#0f1626] border-slate-800 focus-visible:ring-blue-600"
              />
            </div>
          </div>

          {err ? (
            <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {err}
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={loading || !username || !password}
            data-testid="login-submit-button"
            className="w-full bg-blue-600 hover:bg-blue-500 text-white"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-xs text-slate-500 num">
            Session timeout: 8 hours of inactivity.
          </div>
        </form>
      </div>
    </div>
  );
}
